/**
 * Shared platform metadata for the Shoplink surfaces (creator/admin editors,
 * the DSP-ordering screens and the public landing page).
 *
 * These maps used to be inlined — and drifted — in five components. Keep the
 * single copy: `platform` strings are written by the server from found.ee's
 * raw retailer names, so every surface has to normalize them the same way.
 */

/**
 * Platform key for the tenant's OWN storefront (`shop.{tenant}`), written by
 * `server/utils/shoplinkLinks.ts`. Brand-neutral on purpose — the display name
 * and logo come from the tenant theme at render time.
 */
export const OWN_SHOP_PLATFORM = 'shop'

/** found.ee hands back `DEEZER_GEO` / `AMAZON_API_GEO_MUSIC`; manual links are free text. */
export const normalizePlatform = (name?: string | null): string =>
    String(name || '').toLowerCase().replace(/_/g, ' ').trim()

export const isOwnShopPlatform = (name?: string | null): boolean =>
    normalizePlatform(name) === OWN_SHOP_PLATFORM

/**
 * Platform → filename in `/public/assets/dsp-logos`. Every value here must be
 * an asset that actually ships; a missing one renders a broken <img> instead
 * of falling back to the generic link icon.
 */
export const DSP_ICON_MAP: Record<string, string> = {
    'spotify': 'spotify',
    'apple': 'apple-music',
    'apple music': 'apple-music',
    'itunes': 'apple-music',
    'deezer': 'deezer',
    'deezer geo': 'deezer',
    'tidal': 'tidal',
    'amazon': 'amazon',
    'amazon music': 'amazon',
    'amazon api geo music': 'amazon',
    'amazon api store': 'amazon',
    'youtube': 'youtube',
    'youtube video': 'youtube',
    'youtube music': 'youtube',
    'soundcloud': 'soundcloud',
    'qobuz': 'qobuz',
    'tiktok': 'tiktok',
    'instagram': 'instagram',
    'facebook': 'facebook',
    'anghami': 'anghami',
    'awa': 'awa',
    'beatport': 'beatport',
    'jiosaavn': 'jiosaavn',
    'joox': 'joox',
    'kkbox': 'kkbox',
    'line': 'line',
    'mixcloud': 'mixcloud',
    'netease': 'netease',
    'pandora': 'pandora',
    '7digital': '7digital',
    'ami': 'ami',
    'audiblemagic': 'audiblemagic',
    'bmat': 'bmat',
    'fizy': 'fizy',
    'gracenote': 'gracenote',
    'hungama': 'hungama',
    'iheartradio': 'iheartradio',
    'imusica': 'imusica',
    'kuack': 'kuack',
    'nuuday': 'nuuday',
    'peloton': 'peloton',
    'pretzel': 'pretzel',
    'simbals': 'simbals',
    'tencent': 'tencent',
    'touchtunes': 'touchtunes'
}

/**
 * The platforms offered on the DSP-ordering screens, in rough industry order.
 * Our own shop leads — it mirrors `DEFAULT_DSP_ORDER` on the server.
 */
export const ORDERABLE_PLATFORMS: string[] = [
    OWN_SHOP_PLATFORM,
    'spotify', 'apple music', 'deezer', 'tidal', 'amazon', 'youtube music',
    'youtube video', 'soundcloud', 'qobuz', 'tiktok', 'instagram', 'facebook',
    'anghami', 'awa', 'beatport', 'jiosaavn', 'joox', 'kkbox', 'line',
    'mixcloud', 'netease', 'pandora', '7digital', 'ami', 'audiblemagic',
    'bmat', 'fizy', 'gracenote', 'hungama', 'iheartradio', 'imusica', 'kuack',
    'nuuday', 'peloton', 'pretzel', 'simbals', 'tencent', 'touchtunes'
]

/**
 * Display names for every retailer key found.ee writes (stored in capitals:
 * "SPOTIFY", "APPLE", "AMAZON_API_GEO_MUSIC"), keyed by the normalized form.
 * These also go into the shoplink's share text ("Listen to … on Spotify,
 * Apple Music and more"), which read "on SPOTIFY, APPLE" before.
 */
const PLATFORM_NAME_MAP: Record<string, string> = {
    'spotify': 'Spotify',
    'apple': 'Apple Music',
    'apple music': 'Apple Music',
    'itunes': 'iTunes',
    'deezer': 'Deezer',
    'deezer geo': 'Deezer',
    'tidal': 'TIDAL',
    'amazon': 'Amazon Music',
    'amazon music': 'Amazon Music',
    'amazon api geo music': 'Amazon Music',
    'amazon api store': 'Amazon',
    'youtube': 'YouTube',
    'youtube video': 'YouTube',
    'youtube music': 'YouTube Music',
    'soundcloud': 'SoundCloud',
    'qobuz': 'Qobuz',
    'tiktok': 'TikTok',
    'instagram': 'Instagram',
    'facebook': 'Facebook',
    'anghami': 'Anghami',
    'awa': 'AWA',
    'beatport': 'Beatport',
    'traxsource': 'Traxsource',
    'jiosaavn': 'JioSaavn',
    'joox': 'JOOX',
    'kkbox': 'KKBOX',
    'line': 'LINE Music',
    'mixcloud': 'Mixcloud',
    'netease': 'NetEase Cloud Music',
    'pandora': 'Pandora',
    '7digital': '7digital',
    'audiblemagic': 'Audible Magic',
    'bmat': 'BMAT',
    'gracenote': 'Gracenote',
    'hungama': 'Hungama',
    'iheartradio': 'iHeartRadio',
    'imusica': 'iMusica',
    'peloton': 'Peloton',
    'pretzel': 'Pretzel',
    'tencent': 'Tencent',
    'touchtunes': 'TouchTunes',
    'ami': 'AMI',
    'fizy': 'fizy',
    'kuack': 'Kuack',
    'nuuday': 'Nuuday',
    'simbals': 'Simbals'
}

export function useShoplinkPlatforms() {
    const { logoUrl, appName } = useTenantTheme()

    /** Logo URL for a platform, or null when the generic link icon should show. */
    const getDspLogo = (platform?: string | null): string | null => {
        if (!platform) return null
        const normalized = normalizePlatform(platform)

        // Our own storefront is white-label: it wears the tenant's logo rather
        // than a DSP badge shipped in /public.
        if (normalized === OWN_SHOP_PLATFORM) return logoUrl.value

        if (DSP_ICON_MAP[normalized]) return `/assets/dsp-logos/${DSP_ICON_MAP[normalized]}.png`

        // Fuzzy fallback for raw retailer names we haven't mapped verbatim.
        for (const [key, val] of Object.entries(DSP_ICON_MAP)) {
            if (normalized.includes(key)) return `/assets/dsp-logos/${val}.png`
        }

        return null
    }

    const formatPlatformName = (platform?: string | null): string => {
        if (!platform) return ''
        const normalized = normalizePlatform(platform)
        if (normalized === OWN_SHOP_PLATFORM) return `${appName.value} Shop`
        if (PLATFORM_NAME_MAP[normalized]) return PLATFORM_NAME_MAP[normalized]
        // Unmapped. An all-caps key with underscores is a raw retailer key
        // we don't know yet — title-case it instead of shouting. Anything
        // else was typed by a person ("HHV", "JunoDownload"): keep it.
        if (/_/.test(platform) && platform === platform.toUpperCase()) {
            return normalized.replace(/(^|\s)\S/g, c => c.toUpperCase())
        }
        return platform.charAt(0).toUpperCase() + platform.slice(1)
    }

    return { getDspLogo, formatPlatformName }
}
